import { useMemo } from "react";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { useV2ListBusinessUnitsForAccountQuery } from "@generated";
import type {
  ApprovalTrigger,
  ApprovalMeasure,
  ApprovalEvaluation,
} from "../types/ApprovalLevel";

const MEASURE_OPTIONS: { value: ApprovalMeasure; label: string }[] = [
  { value: "contract_cost", label: "Contract Cost" },
  { value: "contract_revenue", label: "Contract Revenue" },
  { value: "contract_margin", label: "Contract Margin" },
  { value: "contract_profit", label: "Contract Profit" },
  { value: "customized_services", label: "Customized Services" },
  { value: "customized_terms", label: "Customized Terms" },
  { value: "net_mrr", label: "Net MRR" },
  { value: "ps_margin", label: "PS Margin" },
  { value: "ms_margin", label: "MS Margin" },
  { value: "payment_credit_amount", label: "Payment Credit Amount" },
  { value: "third_party_margin", label: "Third Party Margin" },
  { value: "third_party_revenue", label: "Third Party Revenue" },
  { value: "total_effort", label: "Total Effort" },
  { value: "business_unit_id", label: "Business Unit" },
];

const EVALUATION_OPTIONS: { value: ApprovalEvaluation; label: string }[] = [
  { value: "greater_than", label: "Greater Than" },
  { value: "equal_to", label: "Equal To" },
  { value: "less_than", label: "Less Than" },
  { value: "between", label: "Between" },
];

export interface TriggersTableProps {
  triggers: ApprovalTrigger[];
  onChange: (triggers: ApprovalTrigger[]) => void;
}

export const TriggersTable = ({ triggers, onChange }: TriggersTableProps) => {
  const { data: businessUnitsData } = useV2ListBusinessUnitsForAccountQuery({});
  const businessUnitOptions = useMemo(
    () =>
      (businessUnitsData?.data ?? []).map((u: any) => ({
        value: String(u.id),
        label: u.attributes?.name ?? "",
      })),
    [businessUnitsData]
  );

  const update = (idx: number, patch: Partial<ApprovalTrigger>) =>
    onChange(triggers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  const remove = (idx: number) =>
    onChange(triggers.filter((_, i) => i !== idx));

  const add = () =>
    onChange([
      ...triggers,
      {
        measure: "",
        evaluation: "",
        comparison_value: "",
        maximum_value: null,
      },
    ]);

  return (
    <>
      <div className="approvalTriggers">
        <div className="approvalTriggers__header">TRIGGERS</div>
        {triggers.length === 0 ? (
          <div className="approvalTriggers__empty">
            Add a trigger to define when this approval level applies.
          </div>
        ) : (
          <div className="approvalTriggers__rows">
            <div className="approvalTriggers__rowHeader">
              <span>Measure</span>
              <span>Evaluation</span>
              <span>Value</span>
              <span>Upper (Between)</span>
              <span />
            </div>
            {triggers.map((t, idx) => (
              <div key={idx} className="approvalTriggers__row">
                <RequiredReactSelect
                  label={null as any}
                  value={
                    t.measure
                      ? MEASURE_OPTIONS.find((o) => o.value === t.measure)
                      : undefined
                  }
                  onChange={(opt: any) => {
                    const measure = (opt?.value ?? "") as ApprovalMeasure | "";
                    update(idx, {
                      measure,
                      comparison_value:
                        measure === t.measure ? t.comparison_value : "",
                    });
                  }}
                  options={MEASURE_OPTIONS}
                  placeholder="Select measure"
                />
                <RequiredReactSelect
                  label={null as any}
                  value={
                    t.evaluation
                      ? EVALUATION_OPTIONS.find((o) => o.value === t.evaluation)
                      : undefined
                  }
                  onChange={(opt: any) => {
                    const evaluation = (opt?.value ?? "") as
                      | ApprovalEvaluation
                      | "";
                    update(idx, {
                      evaluation,
                      maximum_value:
                        evaluation === "between" ? t.maximum_value : null,
                    });
                  }}
                  options={EVALUATION_OPTIONS}
                  placeholder="Select evaluation"
                />
                {t.measure === "business_unit_id" ? (
                  <RequiredReactSelect
                    label={null as any}
                    value={
                      t.comparison_value
                        ? businessUnitOptions.find(
                            (o) => o.value === t.comparison_value
                          )
                        : undefined
                    }
                    onChange={(opt: any) =>
                      update(idx, {
                        comparison_value: opt ? String(opt.value) : "",
                      })
                    }
                    options={businessUnitOptions}
                    placeholder="Select business unit"
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    aria-label={`Trigger ${idx + 1} value`}
                    value={t.comparison_value}
                    onChange={(e) =>
                      update(idx, { comparison_value: e.target.value })
                    }
                  />
                )}
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  aria-label={`Trigger ${idx + 1} upper value`}
                  disabled={t.evaluation !== "between"}
                  value={t.maximum_value ?? ""}
                  onChange={(e) =>
                    update(idx, {
                      maximum_value:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                />
                <Button
                  variant="link"
                  className="text-danger p-0 approvalTriggers__remove"
                  aria-label={`Remove trigger ${idx + 1}`}
                  onClick={() => remove(idx)}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="link"
        type="button"
        onClick={add}
        className="approvalTriggers__addLink"
      >
        Add a Trigger +
      </Button>
    </>
  );
};
